<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden border border-gray-200 dark:border-gray-700">
    <!-- Card Header -->
    <div class="p-4 sm:p-6">
      <div class="flex items-start justify-between">
        <div class="flex-1 min-w-0">
          <!-- Category Name -->
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 truncate">
            {{ category.name }}
          </h3>
          
          <!-- Category Description -->
          <p
            v-if="category.description"
            class="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate"
          >
            {{ category.description }}
          </p>
          
          <!-- Kitchen Visibility Badge -->
          <div class="mt-2">
            <span 
              v-if="getCategoryVisibility(category) === false"
              class="inline-flex items-center gap-1 rounded-full bg-orange-100 dark:bg-orange-900/30 px-2.5 py-0.5 text-xs font-medium text-orange-700 dark:text-orange-300"
              :title="$t('app.forms.visible_in_kitchen_help')"
            >
              <EyeSlashIcon class="h-3 w-3" />
              {{ $t('app.views.menu.categories.not_in_kitchen') }}
            </span>
            <span 
              v-else
              class="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/30 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:text-green-300"
              :title="$t('app.forms.visible_in_kitchen')"
            >
              <EyeIcon class="h-3 w-3" />
              {{ $t('app.views.menu.categories.in_kitchen') }}
            </span>
          </div>
        </div>
        
        <!-- Actions Dropdown -->
        <div
          v-if="platformFeatures.cloudOnly.categoryManagement && category.id && canEdit"
          class="ml-3"
          @click.stop
        >
          <DropdownMenu
            :id="`category-card-${category.id}`"
            button-label="Category actions"
            width="md"
          >
            <DropdownMenuItem
              :icon="PencilIcon"
              variant="default"
              @click="$emit('edit', category)"
            >
              {{ $t('app.actions.edit') }}
            </DropdownMenuItem>
            
            <DropdownMenuDivider />
            
            <DropdownMenuItem
              :icon="TrashIcon"
              variant="danger"
              @click="$emit('delete', category)"
            >
              {{ $t('app.actions.delete') }}
            </DropdownMenuItem>
          </DropdownMenu>
        </div>
      </div>
    </div>
    
    <!-- Card Footer -->
    <div
      v-if="category.created_at"
      class="px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700"
    >
      <p class="text-xs text-gray-500 dark:text-gray-400">
        {{ $t('app.common.created_at') }}: {{ formatDate(category.created_at) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue';
import { PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline';
import type { MenuCategory } from '@/types/menu';
import { platformFeatures } from '@/utils/platform';

defineProps<{
  category: MenuCategory;
  canEdit: boolean;
}>();

defineEmits<{
  edit: [category: MenuCategory];
  delete: [category: MenuCategory];
}>();

// Helper function to safely get category visibility
const getCategoryVisibility = (category: MenuCategory): boolean => {
  return category.visible_in_kitchen ?? true;
};

// Format date helper
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};
</script>
